import { Component } from "react";
import { RouteComponentProps } from "react-router";
import { ResourceType } from "@app/resources";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import TemplateCollectionMetadata, { TemplateMetadata, TemplateSpecialtyGroup } from "@app/templates/templateModels";
import TemplateService from "@app/templates/templateService";
import ServicingCriteriaPanel from "@app/templates/servicing-criteria/ServicingCriteriaPanel";
import AccessCriteriaPanel from "@app/templates/access-criteria/AccessCriteriaPanel";

interface RouteState {
  telehealthSpecialtyGroupCodes: string[];
}

interface TemplateDetailsState extends RouteState {
  loading: boolean;
  templateCollectionMetadata: TemplateCollectionMetadata;
  templateMetadata: TemplateMetadata;
  templateSpecialtyGroups: TemplateSpecialtyGroup[];
}

export default class TemplateDetailsView extends Component<RouteComponentProps<{ templateCollectionId?: string, templateId?: string }>, TemplateDetailsState> {

  private readonly templateService = new TemplateService();

  constructor(props: RouteComponentProps<{ templateCollectionId?: string, templateId?: string }>) {
    super(props);

    const routeState = this.extractRouteState();

    this.state = {
      ...routeState,
      loading: true,
      templateCollectionMetadata: null,
      templateMetadata: null,
      templateSpecialtyGroups: null,
    };
  }

  public async componentDidMount() {
    const templateCollectionId = Number(this.props.match.params.templateCollectionId);

    if (templateCollectionId) {
      const templateCollectionMetadata =
        await this.templateService.getTemplateCollectionById(templateCollectionId);

      const templateMetadata =
        await this.templateService.getTemplateById(templateCollectionId,
          this.props.match.params.templateId,
          true);

      let templateSpecialtyGroups: TemplateSpecialtyGroup[] = null;
      if (templateMetadata) {
        templateSpecialtyGroups =
          await this.templateService.getTemplateSpecialtyGroups(templateCollectionId, this.props.match.params.templateId);
      }

      this.setState({
        loading: false,
        templateCollectionMetadata,
        templateMetadata,
        templateSpecialtyGroups,
      });
    }
  }

  public render() {
    const hasResults = !this.state.loading &&
      this.state.templateCollectionMetadata &&
      this.state.templateMetadata &&
      this.state.templateSpecialtyGroups &&
      this.state.templateSpecialtyGroups.length > 0;

    const title = this.state.loading ?
      "Loading Template..." :
      hasResults ?
        `${this.state.templateCollectionMetadata.resource.name} - ${this.state.templateMetadata.correlationId} Specialty Group Criteria` :
        "Template Not Found";

    return (
      <Page documentTitle="Adequacy Template Details">
        <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkAssessment} items={["Adequacy Template"]} />
        <PageInner>
          <PageHeader title={title}>
            <PageHeaderRight>
              <QESButton qesTestId="template-details-view_go-back" qesType="secondary" onClick={this.handleCancel}>Go Back</QESButton>
            </PageHeaderRight>
          </PageHeader>

          <PageSection>
            <div className="container-fluid">
              {hasResults &&
                <>
                  <hr className="mt0 mb30" />
                  {
                    this.renderAccessServicingCriteriaGroups()
                  }
                </>
              }
            </div>
          </PageSection>
        </PageInner>
      </Page>);
  }

  private extractRouteState: () => RouteState = () => {
    const routeState = (this.props.location.state || {}) as RouteState;

    // Note to the developer: ALWAYS provide a default value. Assume nothing is provided.
    return {
      telehealthSpecialtyGroupCodes: routeState.telehealthSpecialtyGroupCodes ? routeState.telehealthSpecialtyGroupCodes : [],
    } as RouteState;
  }

  private handleCancel = () => {
    this.props.history.goBack();
  }

  private renderAccessServicingCriteriaGroups() {
    const isZipCodeProject = this.state.templateCollectionMetadata && this.state.templateCollectionMetadata.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    return (
      this.state.templateSpecialtyGroups.map((specialtyGroup) => {
        return (
          <div className="container-fluid" key={specialtyGroup.code}>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={specialtyGroup.name}
                testId={`template-details-view__${specialtyGroup.name}-accordion`}
              >
                <AccessCriteriaPanel
                  {...this.props}
                  {...this.state.templateMetadata}
                  className="mt5 mb5"
                  isTelehealthSpecialtyGroup={this.state.telehealthSpecialtyGroupCodes.includes(specialtyGroup.code)}
                  specialtyGroupName={specialtyGroup.name}
                  specialtyGroupCode={specialtyGroup.code}
                />
                {!isZipCodeProject && (
                  <ServicingCriteriaPanel
                    {...this.props}
                    {...this.state.templateMetadata}
                    className="mt5 mb5"
                    servicingCountBy={specialtyGroup.servicingCountBy}
                    servicingMinimumType={specialtyGroup.servicingMinimumType}
                    specialtyGroupName={specialtyGroup.name}
                    specialtyGroupCode={specialtyGroup.code}
                  />
                )}
              </Accordion>
            </AccordionContainer>
          </div>
        );
      })
    );
  }
}
